import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab, Menu, MenuItem, Typography } from '@material-ui/core';
import { TabContext, TabList, TabPanel } from '@material-ui/lab';
import { AppCategory, GlobalAppCollection } from 'models/app';
import styles from './styles.module.scss';
import { EntityId, EntityState } from '@reduxjs/toolkit';
import { prettyCategories ,readableFormat } from '../../utils/transformationHelper';
import { APP_COLLECTION_TABNAME_MAX_LENGTH } from '../../utils/constants';
import { generateRoute } from '../../utils/location';
import { Link as RouteLink } from 'react-router-dom';
import { useLocation } from 'react-router';
import { AppMarketPageIdentifier } from '../../AppRoutes';
import { isPrivateApp } from '../../utils/session';

enum TabType {
  Home = 'home',
  MyApps = 'myapps',
  Categories = 'categories',
  Collections = 'collections'
}

type Tab = {
  name: TabType,
  toRoute?: string,
  openMenu?: boolean,
  usaLocaleOnly?: boolean
  onlyForLoggedInView?:boolean
}

type NavigationHeaderProps = {
  isLoading?: boolean,
  collections: GlobalAppCollection[],
  categories: Maybe<EntityState<AppCategory>>
}

function TabMenuPanel(
  props: PropsWithChildren<{
    value: string;
    open: boolean;
    anchorEl: Element | ((element: Element) => Element) | null | undefined;
    onClose: () => void;
  }>,
): JSX.Element {
  const { children, value, ...moreProps } = props;
  const classes = value === TabType.Categories ? styles.categoriesMenu : value === TabType.Collections ? styles.collectionsMenu : '';
  return (
    <TabPanel value={value}>
      <Menu
        getContentAnchorEl={null} // required in order to override anchorOrigin
        classes={{ paper: classes }}
        elevation={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        {...moreProps}
      >
        {children}
      </Menu>
    </TabPanel>
  );
}


export const NavigationHeader: React.FC<NavigationHeaderProps> = ({isLoading, categories, collections}): JSX.Element => {
  const [tabIdx, setTabIdx] = useState(TabType.Home);
  const [anchorEl, setAnchorEl] = useState<EventTarget | null>(null);
  const { t } = useTranslation();
  const location = useLocation();

  const Tabs: Tab[] = [
    {
      name: TabType.Home,
      toRoute: generateRoute(AppMarketPageIdentifier.Home)
    },
    {
      name: TabType.MyApps,
      toRoute: generateRoute(AppMarketPageIdentifier.MyApps),
      onlyForLoggedInView:true
    },
    {
      name: TabType.Categories,
      toRoute: '',
      openMenu: true,
    },
    {
      name: TabType.Collections,
      toRoute: '',
      openMenu: true
    }
  ];
  const TabMenuPanels: Tab[] = [];

  useEffect(() => {
    closeMenu(TabType.Home);
  }, [location])

  const onTabClick = (
    event: React.ChangeEvent<{}>,
    idx: React.SetStateAction<string>
  ) => {
    setAnchorEl(event.currentTarget);
    setTabIdx(idx as TabType);
  };

  const closeMenu = (tab: TabType):void => {
    setAnchorEl(null);
    setTabIdx(tab);
  };



  return (
    <div className={styles.nav}>
      <TabContext value={tabIdx}>
        <TabList
          onChange={onTabClick}
          textColor="primary"
        >
          {Tabs.filter((tab) => !tab.onlyForLoggedInView || isPrivateApp()).map((tab, index) => {
            if (tab.openMenu){
              TabMenuPanels.push(tab);
            }
            return (
            tab.toRoute ?
              <Tab
                key={index}
                value={tab.name}
                classes={{ root: styles.tab, selected: styles.selectedTab, textColorPrimary: styles.primaryColor }}
                disableFocusRipple={true}
                tabIndex={0}
                label={<h5 className={`${styles.flex} ${styles.tabLabel}`}> {t(`app_nav_tabs.${tab.name}`)}</h5>}
                component={RouteLink}
                to= {tab.toRoute}
                style={{textDecoration:'none'}}
                disableRipple
              />
              :
              <Tab
                key={index}
                value={tab.name}
                classes={{ root: styles.tab, selected: styles.selectedTab, textColorPrimary: styles.primaryColor }}
                disableFocusRipple={true}
                tabIndex={0}
                label={<h5 className={`${styles.flex} ${styles.tabLabel}`}> {t(`app_nav_tabs.${tab.name}`)}</h5>}
                disableRipple
              />
            )
          })}
        </TabList>
        {
          TabMenuPanels.map((tab, index) => {
            if (tab.name === TabType.Collections) {
              return (
                <TabMenuPanel value={tab.name} anchorEl={anchorEl as Element} open={Boolean(anchorEl)} onClose={() => closeMenu(tab.name)} key={index}>
                  {collections?.map((collection) => (
                      <MenuItem key={collection.id} disableRipple classes={{ root: styles.menuItem }}>
                        <RouteLink to={generateRoute(AppMarketPageIdentifier.Collections, collection.id)} key={collection.id}>
                          <Typography color="textPrimary" variant="body1">
                            {readableFormat(collection.displayName,APP_COLLECTION_TABNAME_MAX_LENGTH)}
                          </Typography>
                        </RouteLink>
                      </MenuItem>
                  ))}
                </TabMenuPanel>
              )
            }else if (tab.name === TabType.Categories){
              return (
                <TabMenuPanel value={tab.name} anchorEl={anchorEl as Element} open={Boolean(anchorEl)} onClose={() => closeMenu(tab.name)} key={index}>
                  {!isLoading && categories && categories.ids?.map((appCategoryId:EntityId) => (
                      <MenuItem key={appCategoryId}  classes={{ root: styles.menuItem }}>
                        <RouteLink to={generateRoute(AppMarketPageIdentifier.Categories, appCategoryId as Uuid)} tabIndex={0}>
                          <Typography color="textPrimary" variant="body1">
                           {prettyCategories(categories.entities[appCategoryId]?.name as string, t)}
                          </Typography>
                        </RouteLink>
                      </MenuItem>
                  ))}
                </TabMenuPanel>
              )
            }else return false;
          })
        }
      </TabContext>
    </div>
  );
};
